import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle2, ArrowRight, Fuel, Save, FolderOpen, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUIRED_FIELDS = ['origin', 'destination', 'weight'];
const OPTIONAL_FIELDS = ['transport_mode', 'vehicle_type', 'fuel_type', 'good_name', 'cost_value', 'ghg_category'];
const ALL_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];

const FIELD_LABELS = {
  origin: 'Origin (From)',
  destination: 'Destination (To)',
  weight: 'Weight',
  transport_mode: 'Transport Mode',
  vehicle_type: 'Vehicle Type',
  fuel_type: 'Fuel Type (defaults to Diesel)',
  good_name: 'Goods Name',
  cost_value: 'Cost',
  ghg_category: 'GHG Category',
};

const FUEL_OPTIONS = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'petrol', label: 'Petrol' },
  { value: 'cng', label: 'CNG' },
];

const DEFAULT_VEHICLE_TYPES = {
  road: 'hgv_all_hgvs_average',
  rail: 'freight_train',
  air: 'international_with_rf', // DESNZ "International, to/from non-UK"
  water: 'cargo_container_ship_average',
};

export default function ColumnMapper({
  columns,
  sampleRows,
  proposedMapping,
  confirmedMapping,
  isSubmitting,
  onConfirm,
  onBack,
}) {
  // Initialize mapping: use saved mapping if returning via back nav, else AI proposal
  const initMapping = () => {
    if (confirmedMapping?.confirmed_mapping) {
      const m = {};
      for (const field of ALL_FIELDS) {
        if (confirmedMapping.confirmed_mapping[field]) {
          m[field] = confirmedMapping.confirmed_mapping[field];
        }
      }
      return m;
    }
    const m = {};
    if (proposedMapping) {
      for (const field of ALL_FIELDS) {
        if (proposedMapping[field]?.column) {
          m[field] = proposedMapping[field].column;
        }
      }
    }
    return m;
  };

  const [mapping, setMapping] = useState(initMapping);
  const [defaultFuelType, setDefaultFuelType] = useState(
    confirmedMapping?.default_fuel_type || 'diesel'
  );
  const [weightUnit, setWeightUnit] = useState(
    confirmedMapping?.confirmed_mapping?.weight_unit || proposedMapping?.weight_unit || 'kg'
  );

  // Template state
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    axios.get(`${API}/agent/templates`).then(res => setTemplates(res.data)).catch(() => {});
  }, []);

  const applyTemplate = (template) => {
    const tmpl = template.mapping_json;
    const m = {};
    if (tmpl.mapping) {
      for (const field of ALL_FIELDS) {
        if (tmpl.mapping[field] && columns.includes(tmpl.mapping[field])) {
          m[field] = tmpl.mapping[field];
        }
      }
    }
    setMapping(m);
    if (tmpl.default_fuel_type) setDefaultFuelType(tmpl.default_fuel_type);
    if (tmpl.mapping?.weight_unit) setWeightUnit(tmpl.mapping.weight_unit);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      const confirmed = {};
      for (const field of ALL_FIELDS) {
        if (mapping[field]) confirmed[field] = mapping[field];
      }
      const res = await axios.post(`${API}/agent/templates`, {
        name: templateName.trim(),
        mapping: { ...confirmed, weight_unit: weightUnit },
        default_fuel_type: defaultFuelType,
        default_vehicle_types: DEFAULT_VEHICLE_TYPES,
      });
      setTemplates(prev => [res.data, ...prev]);
      setShowSaveTemplate(false);
      setTemplateName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/agent/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const updateMapping = (field, column) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (column === '__none__') {
        delete next[field];
      } else {
        next[field] = column;
      }
      return next;
    });
  };

  const unmappedRequired = REQUIRED_FIELDS.filter((f) => !mapping[f]);
  const canConfirm = unmappedRequired.length === 0;

  const getConfidence = (field) => {
    return proposedMapping?.[field]?.confidence;
  };

  const handleConfirm = () => {
    // Backend ConfirmedMapping expects plain strings (column names), not objects
    const confirmed = {};
    for (const field of ALL_FIELDS) {
      if (mapping[field]) {
        confirmed[field] = mapping[field];
      }
    }
    confirmed.weight_unit = weightUnit;
    onConfirm({
      confirmed_mapping: confirmed,
      default_fuel_type: defaultFuelType,
      default_vehicle_types: DEFAULT_VEHICLE_TYPES,
    });
  };

  const [showSampleData, setShowSampleData] = useState(true);

  return (
    <div className="space-y-6">
      {/* Saved Templates */}
      {templates.length > 0 && (
        <Card className="pro-card">
          <CardHeader className="pb-3 bg-gradient-to-r from-amber-50 to-white border-b border-amber-100">
            <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-amber-600" />
              Saved Templates
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <div className="flex flex-wrap gap-2">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                  <button
                    onClick={() => applyTemplate(t)}
                    className="text-sm font-medium text-amber-800 hover:text-amber-900"
                  >
                    {t.name}
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.id)}
                    className="text-amber-400 hover:text-red-500 ml-1"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mapping Table with integrated sample data */}
      <Card className="pro-card">
        <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-gray-800">
                Column Mapping
              </CardTitle>
              <p className="text-sm text-gray-500">
                Map your spreadsheet columns to CarbonRoute fields. AI has pre-filled suggestions.
              </p>
            </div>
            {sampleRows?.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSampleData(!showSampleData)}
                className="text-xs text-gray-500"
              >
                {showSampleData ? 'Hide' : 'Show'} Sample Data
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          {/* Inline sample data preview */}
          {showSampleData && sampleRows?.length > 0 && (
            <div className="overflow-x-auto mb-4 border border-gray-200 rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col} className="text-left px-2 py-1.5 bg-gray-100 font-medium text-gray-600 whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sampleRows.slice(0, 3).map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      {columns.map((col) => (
                        <td key={col} className="px-2 py-1.5 text-gray-700 whitespace-nowrap">
                          {row[col] || '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {ALL_FIELDS.map((field) => {
            const isRequired = REQUIRED_FIELDS.includes(field);
            const isMapped = !!mapping[field];
            const confidence = getConfidence(field);

            return (
              <div
                key={field}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  isRequired && !isMapped
                    ? 'border-red-200 bg-red-50'
                    : isMapped
                    ? 'border-green-200 bg-green-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {/* Field label */}
                <div className="w-40 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-700">
                      {FIELD_LABELS[field]}
                    </span>
                    {isRequired && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                        Required
                      </Badge>
                    )}
                  </div>
                </div>

                <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />

                {/* Column selector */}
                <div className="flex-1">
                  <Select
                    value={mapping[field] || '__none__'}
                    onValueChange={(v) => updateMapping(field, v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select column..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Not mapped —</SelectItem>
                      {columns.map((col) => (
                        <SelectItem key={col} value={col}>
                          {col}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Confidence indicator */}
                <div className="w-16 flex-shrink-0 text-right">
                  {isMapped && confidence != null && (
                    <span
                      className={`text-xs font-medium ${
                        confidence >= 0.8
                          ? 'text-green-600'
                          : confidence >= 0.5
                          ? 'text-amber-600'
                          : 'text-red-600'
                      }`}
                    >
                      {Math.round(confidence * 100)}%
                    </span>
                  )}
                  {isMapped && (
                    <CheckCircle2 className="w-4 h-4 text-green-500 inline ml-1" />
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Defaults */}
      <Card className="pro-card">
        <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
          <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <Fuel className="w-4 h-4" />
            Upload Defaults
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Default Fuel Type
              </label>
              <Select value={defaultFuelType} onValueChange={setDefaultFuelType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FUEL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400 mt-1">
                Used when vehicle type doesn't specify fuel
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Weight Unit
              </label>
              <Select value={weightUnit} onValueChange={setWeightUnit}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kg">Kilograms (kg)</SelectItem>
                  <SelectItem value="tons">Metric Tonnes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Template Inline */}
      {showSaveTemplate && (
        <Card className="pro-card border-sky-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name (e.g., 'Weekly Shipments')"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                maxLength={100}
              />
              <Button onClick={saveTemplate} disabled={savingTemplate || !templateName.trim()} size="sm" className="bg-sky-600 hover:bg-sky-700">
                {savingTemplate ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowSaveTemplate(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <div className="flex items-center gap-3">
          {unmappedRequired.length > 0 && (
            <span className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4" />
              Missing: {unmappedRequired.map((f) => FIELD_LABELS[f]).join(', ')}
            </span>
          )}
          {canConfirm && !showSaveTemplate && !isSubmitting && (
            <Button variant="outline" onClick={() => setShowSaveTemplate(true)} size="sm">
              <Save className="w-4 h-4 mr-1" />
              Save as Template
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || isSubmitting}
            className="bg-sky-600 hover:bg-sky-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              'Start Calculation'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
